import * as THREE from "three";
import { GAME } from "../config/constants.js";
import { rollLoot } from "./LootTables.js";
import { affinityFor } from "../config/relics.js";
import { addItem, canAdd, takeOne } from "./Inventory.js";

// Ground loot self-destructs after this long (blinks out over the last 5s).
const LOOT_TTL = 30;

// Which model represents each item on the ground.
function pickupModelKey(item) {
  if (item.model) return item.model;
  if (item.kind === "armor") return "weapon/shield";
  return null;
}

/**
 * LootSystem spawns floating pickups (real item models when available, else a
 * rarity gem), animates them, and handles pickup (F). Coins are auto-banked as
 * gold on pickup; everything else goes to the bag.
 */
export class LootSystem {
  constructor(scene, registry) {
    this.scene = scene;
    this.registry = registry;
    this.pickups = [];
    this.luck = 1;   // loot fortune multiplier (research); set by Game each floor
    this.onDrop = null;   // optional hook(item) fired whenever an item hits the ground
  }

  clear() { for (const p of this.pickups) this.scene.remove(p.mesh); this.pickups = []; }

  /** Roll the enemy table on a kill (gated by drop chance). */
  maybeDrop(x, z, floor, y = 0) {
    if (Math.random() > GAME.lootDropChance * (this.luck || 1)) return;
    this.dropItem(rollLoot("enemy", floor), x, z, y);
  }

  /** Spawn a specific item as a floating pickup. */
  dropItem(item, x, z, y = 0) {
    if (!item) return;
    const color = new THREE.Color(item.rarity ? item.rarity.color : "#c8c8c8");
    const group = new THREE.Group();
    const spinner = new THREE.Group(); spinner.position.y = 1.1; group.add(spinner);

    const key = pickupModelKey(item);
    if (key && this.registry && this.registry.has(key)) {
      const m = this.registry.instance(key);
      // Food / treasure / fragment models come from packs authored at tiny
      // real-world scale — normalize them by bounding box so they read on the
      // ground; KayKit gear is already ~1 unit and uses a fixed scale.
      if (item.kind === "food" || item.kind === "treasure" || item.kind === "fragment") {
        m.updateWorldMatrix(true, true);
        const sz = new THREE.Box3().setFromObject(m).getSize(new THREE.Vector3());
        const maxd = Math.max(sz.x, sz.y, sz.z) || 1;
        m.scale.setScalar(0.85 / maxd);
      } else {
        m.scale.setScalar(item.kind === "coins" || item.kind === "potion" ? 1.6 : 1.4);
      }
      m.traverse((o) => { if (o.isMesh) o.castShadow = true; });
      spinner.add(m);
    } else {
      const gem = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.32),
        new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.6, roughness: 0.3 })
      );
      spinner.add(gem);
    }

    // NOTE: no per-drop PointLight — changing the scene light count forces
    // Three.js to recompile every material, which causes hitches on loot bursts.
    // The emissive gem/model + additive beam read fine under the ambient light.
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.06, 3, 6),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.28, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    beam.position.y = 1.5;
    group.add(beam);
    group.position.set(x, y, z);
    this.scene.add(group);
    // Vault Keys never time out — a key you spotted across the room should still
    // be there when you fight your way over to it.
    const ttl = item.kind === "key" ? Infinity : LOOT_TTL;
    this.pickups.push({ mesh: group, spinner, item, x, z, t: Math.random() * 6.28, ttl });
    if (this.onDrop) { try { this.onDrop(item); } catch (_) {} }
  }

  update(dt) {
    for (const p of this.pickups) {
      p.t += dt * 3;
      p.spinner.rotation.y = p.t;
      p.spinner.position.y = 1.1 + Math.sin(p.t) * 0.15;
      p.ttl -= dt;
      if (p.ttl < 5) p.mesh.visible = Math.floor(p.ttl * 5) % 2 === 0;  // blink before vanishing
    }
    // expire ground loot that's been ignored too long
    this.pickups = this.pickups.filter((p) => {
      if (p.ttl <= 0) { this.scene.remove(p.mesh); return false; }
      return true;
    });
  }

  /** Auto-collect every pickup within `radius` (used by the familiar's magnet).
   *  Coins bank to gold; other items go to the bag if there's room, else stay. */
  collectWithin(player, radius) {
    const got = [];
    for (let i = this.pickups.length - 1; i >= 0; i--) {
      const p = this.pickups[i];
      if (Math.hypot(p.x - player.pos.x, p.z - player.pos.z) > radius) continue;
      const banked = p.item.kind === "coins" || p.item.kind === "key";
      if (!banked && !canAdd(player.inv, p.item, player.invCap || 20)) continue; // no room → leave it
      this.scene.remove(p.mesh);
      this.pickups.splice(i, 1);
      if (p.item.kind === "coins") player.gold = (player.gold || 0) + p.item.amount;
      else if (p.item.kind === "key") player.keys = (player.keys || 0) + (p.item.amount || 1);
      else addItem(player.inv, p.item, player.invCap || 20);
      got.push(p.item);
    }
    return got;
  }

  /** Pick up nearest; coins bank to gold, everything else to the bag. */
  tryPickup(player) {
    let best = -1, bestD = 1.7;
    for (let i = 0; i < this.pickups.length; i++) {
      const p = this.pickups[i];
      const d = Math.hypot(p.x - player.pos.x, p.z - player.pos.z);
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best < 0) return null;
    const p = this.pickups[best];
    // coins → gold and keys → key count both bank straight to a counter; every-
    // thing else stacks or needs a free bag slot
    const banked = p.item.kind === "coins" || p.item.kind === "key";
    if (!banked && !canAdd(player.inv, p.item, player.invCap || 20)) {
      return { full: true, name: p.item.name };   // leave it on the ground
    }
    this.scene.remove(p.mesh);
    this.pickups.splice(best, 1);
    if (p.item.kind === "coins") player.gold = (player.gold || 0) + p.item.amount;
    else if (p.item.kind === "key") player.keys = (player.keys || 0) + (p.item.amount || 1);
    else addItem(player.inv, p.item, player.invCap || 20);
    return p.item;
  }
}

/** Equip weapon/armor; swaps and re-queues old gear, then refreshes the hero. */
export function equipItem(player, idx) {
  const it = player.inv[idx];
  if (!it || it.kind === "potion" || it.kind === "coins" || it.kind === "fragment") return null;
  // Named relics may refuse a hero they aren't attuned to.
  if (it.kind === "weapon" && it.named) {
    const aff = affinityFor(it, player.classId, player.attuneAny);
    if (!aff.wield) return { refused: true, note: aff.note || "The relic refuses your hand." };
  }
  let old;
  if (it.kind === "weapon") { old = player.weapon; player.weapon = it; player.applyGear(); }
  else { old = player.armor; player.armor = it; player.applyGear(); }
  player.inv[idx] = old || null;   // the swapped-out gear takes the slot the new item left
  player.refreshPlaceholderColors?.();
  return it;
}

/** Consume a potion from the bag (heal or restore mana). Returns the item used or null. */
export function useItem(player, idx) {
  const it = player.inv[idx];
  if (!it || it.kind !== "potion") return null;
  if (it.potType === "mana" || it.mana) player.mana = Math.min(player.maxMana, player.mana + (it.mana || 0));
  else player.hp = Math.min(player.maxHp, player.hp + (it.heal || 0));
  takeOne(player.inv, idx);   // consume one from the stack
  return it;
}
